import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Modal from "../components/Modal";
import TreatmentSelect from "../components/TreatmentSelect";
import { inputCls, labelCls, toApiTime } from "../utils/datetime";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  CalendarDays, Banknote, Receipt, UserPlus, Users, BellRing, Stethoscope, Package,
  UserCheck, TrendingUp, Star, Brain, ArrowRight, ShoppingCart, AlertTriangle, XCircle, Clock,
  FileSpreadsheet, Mail,
} from "lucide-react";
import api, { inr } from "../api";

const STATUS_STYLE = {
  Confirmed: "bg-teal-soft text-teal-brand",
  "In Progress": "bg-blue-50 text-blue-600",
  Upcoming: "bg-gold-soft/60 text-pine-deep",
  Completed: "bg-cream text-pine/60",
  Cancelled: "bg-red-50 text-red-500",
};

function Stat({ icon: Icon, label, value, sub, tint = "bg-teal-soft text-teal-brand", testid, onClick }) {
  const cls = "rounded-2xl bg-white border border-pine/8 shadow-soft p-4 flex flex-col gap-2 hover:-translate-y-0.5 hover:shadow-lift transition-all duration-300 text-left w-full";
  const inner = (
    <>
      <div className="flex items-center gap-2.5">
        <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${tint}`}><Icon className="h-4.5 w-4.5 h-5 w-5" strokeWidth={1.9} /></span>
        <p className="text-xs font-semibold text-slate-500 leading-tight">{label}</p>
      </div>
      <p className="font-display text-xl md:text-2xl font-semibold text-pine-deep">{value}</p>
      <p className="text-[11px] font-semibold text-teal-brand">{sub}</p>
    </>
  );
  if (onClick) return <button type="button" data-testid={testid} onClick={onClick} className={cls}>{inner}</button>;
  return <div data-testid={testid} className={cls}>{inner}</div>;
}

export default function Dashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [todays, setTodays] = useState([]);
  const [activities, setActivities] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [showToday, setShowToday] = useState(false);
  const [showWalkin, setShowWalkin] = useState(false);
  const [walkinForm, setWalkinForm] = useState({ name: "", phone: "", treatment: "", time: "", notes: "" });
  const [walkinBusy, setWalkinBusy] = useState(false);
  const me = JSON.parse(localStorage.getItem("dyntra_connect_user") || "{}");
  const canExport = ["super_admin", "owner", "admin"].includes(me.level);

  const submitWalkin = async (e) => {
    e.preventDefault();
    setWalkinBusy(true);
    try {
      const r = await api.post("/connect/walkin", { ...walkinForm, time: toApiTime(walkinForm.time) });
      toast.success(`Walk-in registered — ${r.data.patient.patient_id} · appointment added to today's schedule`);
      setWalkinForm({ name: "", phone: "", treatment: "", time: "", notes: "" });
      setShowWalkin(false);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not register walk-in");
    }
    setWalkinBusy(false);
  };

  const downloadExcel = async () => {
    setExporting(true);
    try {
      const r = await api.get("/connect/export/excel", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `dyntra-report-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel report downloaded");
    } catch {
      toast.error("Could not generate the Excel report");
    }
    setExporting(false);
  };

  const emailReport = async () => {
    setEmailing(true);
    try {
      const r = await api.post("/connect/export/email");
      const to = (r.data.recipients || []).join(" & ");
      if (r.data.attachment) toast.success(`Excel report emailed to ${to}`);
      else toast.info(`Report summary emailed to ${to} — attachment not supported by the mailer, use Export Excel to download`);
    } catch {
      toast.error("Could not email the report");
    }
    setEmailing(false);
  };

  const loadAll = () => {
    api.get("/connect/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/connect/appointments?today=1").then((r) => setTodays(r.data)).catch(() => {});
    api.get("/connect/activities").then((r) => setActivities(r.data)).catch(() => {});
  };

  useEffect(() => {
    loadAll();
    window.addEventListener("clinic-changed", loadAll);
    return () => window.removeEventListener("clinic-changed", loadAll);
  }, []);

  if (!stats) return <p className="text-sm text-slate-400">Loading dashboard...</p>;

  return (
    <div className="space-y-6" data-testid="connect-dashboard">
      {canExport && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white border border-pine/8 shadow-soft px-5 py-4">
          <div>
            <p className="font-display text-base font-semibold text-pine-deep">Clinic Excel Report</p>
            <p className="text-[11px] text-slate-500">Patients, appointments, invoices, inventory & leads — for the clinic you are viewing</p>
          </div>
          <div className="flex gap-2.5">
            <button data-testid="export-excel-btn" onClick={downloadExcel} disabled={exporting}
              className="inline-flex items-center gap-2 rounded-full bg-pine-deep px-4 py-2 text-xs font-bold text-ivory hover:bg-pine transition-colors disabled:opacity-50">
              <FileSpreadsheet className="h-4 w-4" /> {exporting ? "Preparing..." : "Export Excel"}
            </button>
            <button data-testid="email-report-btn" onClick={emailReport} disabled={emailing}
              className="inline-flex items-center gap-2 rounded-full border border-teal-brand/30 bg-teal-soft px-4 py-2 text-xs font-bold text-teal-brand hover:bg-teal-brand hover:text-white transition-colors disabled:opacity-50">
              <Mail className="h-4 w-4" /> {emailing ? "Sending..." : "Email Report"}
            </button>
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-2.5">
        <Link to="/connect/app/patients" data-testid="quick-new-patient"
          className="inline-flex items-center gap-2 rounded-full bg-teal-brand px-5 py-2.5 text-xs font-bold text-white hover:bg-pine transition-colors">
          <UserPlus className="h-4 w-4" /> New Patient
        </Link>
        <button data-testid="quick-walkin" onClick={() => setShowWalkin(true)}
          className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-xs font-bold text-pine-deep shadow-lift hover:-translate-y-0.5 transition-all">
          <Clock className="h-4 w-4" /> Walk-in Patient
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <Stat testid="stat-today-appointments" icon={CalendarDays} label="Today's Appointments" value={stats.today_appointments} sub="Tap to view list" onClick={() => setShowToday(true)} />
        <Stat testid="stat-today-revenue" icon={Banknote} label="Today's Revenue" value={inr(stats.today_revenue)} sub="View billing" tint="bg-gold-soft/70 text-gold" onClick={() => nav("/connect/app/billing")} />
        <Stat testid="stat-pending" icon={Receipt} label="Pending Payments" value={inr(stats.pending_payments)} sub={`${stats.pending_invoices} Invoices — tap to collect`} tint="bg-orange-50 text-orange-500" onClick={() => nav("/connect/app/billing")} />
        <Stat testid="stat-new-patients" icon={UserPlus} label="New Patients" value={stats.new_patients} sub="View patients" tint="bg-blue-50 text-blue-500" onClick={() => nav("/connect/app/patients")} />
        <Stat testid="stat-returning" icon={Users} label="Returning Patients" value={stats.returning_patients} sub="View patients" tint="bg-violet-50 text-violet-500" onClick={() => nav("/connect/app/patients")} />
        <Stat testid="stat-followups" icon={BellRing} label="Follow-ups Due" value={stats.followups_due} sub="View follow-ups" tint="bg-red-50 text-red-500" onClick={() => nav("/connect/app/followups")} />
        <Stat testid="stat-treatments" icon={Stethoscope} label="Treatments in Progress" value={stats.treatments_in_progress} sub="View appointments" onClick={() => nav("/connect/app/appointments")} />
        <Stat testid="stat-low-stock" icon={Package} label="Low Stock Items" value={stats.low_stock + stats.out_of_stock} sub="View inventory" tint="bg-gold-soft/70 text-gold" onClick={() => nav("/connect/app/inventory")} />
        <Stat testid="stat-staff" icon={UserCheck} label="Staff Present Today" value={`${stats.staff_present} / ${stats.staff_total}`} sub={`${stats.staff_total ? Math.round((stats.staff_present / stats.staff_total) * 100) : 0}% Present`} onClick={() => nav("/connect/app/staff")} />
        <Stat testid="stat-monthly-revenue" icon={TrendingUp} label="Monthly Revenue" value={stats.finance_restricted ? "Restricted" : inr(stats.monthly_revenue)} sub={stats.finance_restricted ? "Owner access only" : "This month"} tint="bg-teal-soft text-teal-brand" />
        <Stat testid="stat-performance" icon={Star} label="Clinic Performance" value={stats.clinic_performance ? `${stats.clinic_performance}%` : "—"} sub="Based on activity" tint="bg-violet-50 text-violet-500" />
        <Link to="/connect/app/ai-insights" data-testid="stat-ai-insights" className="rounded-2xl bg-pine-deep text-ivory shadow-soft p-4 flex flex-col gap-2 hover:-translate-y-0.5 hover:shadow-grand transition-all duration-300">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 text-gold"><Brain className="h-5 w-5" strokeWidth={1.9} /></span>
            <p className="text-xs font-semibold text-ivory/70">AI Business Insights</p>
          </div>
          <p className="mt-auto inline-flex items-center gap-1.5 text-sm font-bold text-gold">View Insights <ArrowRight className="h-4 w-4" /></p>
        </Link>
      </div>

      <div className="grid xl:grid-cols-12 gap-5">
        <div className="xl:col-span-4 rounded-2xl bg-white border border-pine/8 shadow-soft p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold text-pine-deep">Today's Appointments</h3>
            <Link to="/connect/app/appointments" className="text-xs font-bold text-teal-brand inline-flex items-center gap-1 hover:text-pine">View Calendar <ArrowRight className="h-3 w-3" /></Link>
          </div>
          <div className="space-y-3">
            {todays.slice(0, 6).map((a) => (
              <div key={a.id} className="flex items-center gap-3 rounded-xl border border-pine/6 p-3 hover:border-teal-brand/30 transition-colors">
                <span className="text-[11px] font-bold text-teal-brand w-14 shrink-0">{a.time}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-pine-deep truncate">{a.name}</p>
                  <p className="text-[11px] text-slate-500 truncate">{a.service} · {a.doctor || "Dr. Ankita Pandey"}</p>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${STATUS_STYLE[a.status] || STATUS_STYLE.Upcoming}`}>{a.status || "Upcoming"}</span>
              </div>
            ))}
            {todays.length === 0 && <p className="text-sm text-slate-400">No appointments today yet.</p>}
          </div>
        </div>

        <div className="xl:col-span-4 rounded-2xl bg-white border border-pine/8 shadow-soft p-5">
          {stats.finance_restricted ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-10">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-cream text-pine/50"><TrendingUp className="h-6 w-6" /></span>
              <h3 className="mt-4 font-display text-lg font-semibold text-pine-deep">Revenue Overview</h3>
              <p className="mt-1.5 text-xs text-slate-500 max-w-[220px]">Weekly and monthly finance details are visible to the Clinic Owner and Admin roles only.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-display text-lg font-semibold text-pine-deep">Revenue Overview</h3>
                <span className="rounded-full border border-pine/10 px-3 py-1 text-[11px] font-bold text-pine/60">This Year</span>
              </div>
              <p className="text-xs text-slate-500">Total Revenue</p>
              <p className="font-display text-3xl font-semibold text-pine-deep">{inr(stats.monthly_revenue)}</p>
              <p className="text-[11px] font-bold text-teal-brand mb-3">vs last month</p>
              <div className="h-44">
                {stats.revenue_series.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={stats.revenue_series}>
                      <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                      <YAxis hide domain={["auto", "auto"]} />
                      <Tooltip formatter={(v) => inr(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb", fontSize: 12 }} />
                      <Line type="monotone" dataKey="value" stroke="#0E7C86" strokeWidth={2.5} dot={{ r: 3, fill: "#0E7C86" }} activeDot={{ r: 5, fill: "#C9A24B" }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-center">
                    <p className="text-xs text-slate-400 max-w-[220px]">No revenue data yet — it will appear here as you create invoices.</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="xl:col-span-2 rounded-2xl bg-white border border-pine/8 shadow-soft p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-base font-semibold text-pine-deep">Inventory Alerts</h3>
            <Link to="/connect/app/inventory" className="text-[11px] font-bold text-teal-brand">View All</Link>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2.5"><AlertTriangle className="h-4 w-4 text-amber-500" /><div><p className="font-bold text-pine-deep text-[13px]">Low Stock Items</p><p className="text-[11px] text-slate-500">{stats.low_stock} items</p></div></div>
            <div className="flex items-center gap-2.5"><Clock className="h-4 w-4 text-orange-500" /><div><p className="font-bold text-pine-deep text-[13px]">Expiring Soon</p><p className="text-[11px] text-slate-500">{stats.expiring_soon} items</p></div></div>
            <div className="flex items-center gap-2.5"><XCircle className="h-4 w-4 text-red-500" /><div><p className="font-bold text-pine-deep text-[13px]">Out of Stock</p><p className="text-[11px] text-slate-500">{stats.out_of_stock} items</p></div></div>
            <div className="flex items-center gap-2.5"><Package className="h-4 w-4 text-teal-brand" /><div><p className="font-bold text-pine-deep text-[13px]">Reorder Suggested</p><p className="text-[11px] text-slate-500">{stats.reorder_suggested} items</p></div></div>
          </div>
        </div>

        <div className="xl:col-span-2 rounded-2xl bg-white border border-pine/8 shadow-soft p-5">
          <p className="font-display text-base font-semibold text-pine-deep mb-4">Recent Activities</p>
          <div className="space-y-3.5">
            {activities.slice(0, 5).map((a) => (
              <div key={a.id} className="flex gap-2.5">
                <span className="mt-1 h-2 w-2 rounded-full bg-teal-brand shrink-0" />
                <div>
                  <p className="text-[12px] font-semibold text-pine-deep leading-snug">{a.text}</p>
                  <p className="text-[10px] text-slate-400">{a.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid xl:grid-cols-12 gap-5">
        <div className="xl:col-span-8 rounded-2xl bg-white border border-pine/8 shadow-soft p-5">
          <h3 className="font-display text-lg font-semibold text-pine-deep mb-4">AI Insights for Your Clinic</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { label: "Today's Revenue", value: inr(stats.today_revenue), sub: `${stats.today_appointments} appointment(s) today`, tint: "text-teal-brand" },
              { label: "Walk-ins Today", value: stats.walkins_today ?? 0, sub: "Registered at the front desk", tint: "text-pine-deep" },
              { label: "Low Stock Watch", value: stats.low_stock + stats.out_of_stock, sub: "Items at or below reorder level", tint: "text-amber-600" },
              { label: "Follow-ups Due", value: stats.followups_due, sub: "Patients expecting a call-back", tint: "text-teal-brand" },
            ].map((c) => (
              <div key={c.label} className="rounded-xl bg-cream/60 p-4">
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{c.label}</p>
                <p className={`mt-2 text-sm font-bold leading-snug ${c.tint}`}>{c.value}</p>
                <p className="mt-1 text-[11px] text-slate-500">{c.sub}</p>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[10px] text-slate-400">Live numbers from your clinic's actual records — updated on every load.</p>
        </div>
        <div className="xl:col-span-4 rounded-2xl bg-pine-deep text-ivory shadow-grand p-6 relative overflow-hidden grain">
          <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-gold">Dyntra Store</p>
          <h3 className="mt-2 font-display text-xl font-semibold">Low on stock?</h3>
          <p className="mt-2 text-sm text-ivory/70">Order from Dyntra Store and get the best dental products delivered.</p>
          <div className="mt-5 flex gap-3">
            <a href="https://dyntra.store" target="_blank" rel="noopener noreferrer" data-testid="dash-store-order" className="inline-flex items-center gap-2 rounded-full bg-teal-brand px-5 py-2.5 text-xs font-bold text-white hover:bg-teal-brand/90 transition-colors">
              <ShoppingCart className="h-4 w-4" /> Order Now
            </a>
            <a href="https://dyntra.store" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-bold text-gold hover:text-gold-soft transition-colors self-center">Go to Store <ArrowRight className="h-3.5 w-3.5" /></a>
          </div>
        </div>
      </div>

      <p className="text-center text-[11px] text-slate-400 pt-2">© 2026 Dyntra Connect · A unit of DyntraStore Pvt. Ltd.</p>

      {showToday && (
        <Modal testid="today-appointments-modal" wide onClose={() => setShowToday(false)}
          title="Today's Appointments" subtitle={`${todays.length} scheduled today`}>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {todays.map((a) => (
              <div key={a.id} data-testid={`today-modal-${a.id.slice(0, 8)}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-pine/8 px-4 py-3">
                <div>
                  <p className="text-sm font-bold text-pine-deep">{a.name} {a.patient_id && <span className="text-[10px] text-gold font-bold">{a.patient_id}</span>}</p>
                  <p className="text-xs text-slate-500">{a.service} · {a.phone}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-bold text-teal-brand">{a.time}</p>
                  <p className="text-[10px] text-slate-400">{a.status || "Upcoming"}</p>
                </div>
              </div>
            ))}
            {todays.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No appointments today.</p>}
          </div>
        </Modal>
      )}

      {showWalkin && (
        <Modal testid="walkin-modal" onClose={() => setShowWalkin(false)} title="Walk-in Patient" subtitle="Registers the patient AND adds them to today's schedule in one step">
          <form onSubmit={submitWalkin} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Name *</label>
                <input required minLength={2} data-testid="walkin-name" value={walkinForm.name} onChange={(e) => setWalkinForm({ ...walkinForm, name: e.target.value })} className={`mt-1 ${inputCls}`} />
              </div>
              <div>
                <label className={labelCls}>Phone *</label>
                <input required type="tel" inputMode="tel" data-testid="walkin-phone" value={walkinForm.phone} onChange={(e) => setWalkinForm({ ...walkinForm, phone: e.target.value })} className={`mt-1 ${inputCls}`} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Treatment</label>
              <div className="mt-1"><TreatmentSelect testid="walkin-treatment" value={walkinForm.treatment} onChange={(v) => setWalkinForm({ ...walkinForm, treatment: v })} /></div>
            </div>
            <div>
              <label className={labelCls}>Time (defaults to now)</label>
              <input type="time" data-testid="walkin-time" value={walkinForm.time} onChange={(e) => setWalkinForm({ ...walkinForm, time: e.target.value })} className={`mt-1 ${inputCls}`} />
            </div>
            <div>
              <label className={labelCls}>Notes</label>
              <textarea data-testid="walkin-notes" rows={2} value={walkinForm.notes} onChange={(e) => setWalkinForm({ ...walkinForm, notes: e.target.value })}
                className="mt-1 w-full rounded-xl border border-pine/15 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine bg-white" />
            </div>
            <button type="submit" disabled={walkinBusy} data-testid="walkin-submit"
              className="w-full rounded-full bg-pine px-5 py-3 text-sm font-bold text-ivory hover:bg-pine-deep transition-colors disabled:opacity-50">
              {walkinBusy ? "Registering…" : "Register Walk-in"}
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}
